/**
 * V1 Recipient Resolver — resolves recipient policies to typed recipients
 * with FHIR references. Email addresses are resolved when available but
 * are not required for in-app notification delivery.
 *
 * V1 supported notification policies:
 *   - client-primary (alias: client-primary-email)
 *   - appointment-participants (Appointment-triggered; Patient + RelatedPerson)
 *   - care-team-primary-contact
 *   - guardian-or-responsible-party
 *   - assigned-provider (Appointment-triggered rules only)
 *   - task-owner (Task-triggered rules only)
 *
 * Legacy/unsupported (resolve as unresolved at runtime):
 *   - billing-contact
 *   - contact-form-submitter
 */
import type { MedplumClient } from '@medplum/core';
import type { Appointment, Patient, Practitioner, PractitionerRole, RelatedPerson } from '@medplum/fhirtypes';

import {
  getPatientParticipants,
  getPractitionerParticipant,
  getRelatedPersonParticipants,
} from '@unify/fhir-model/resources/appointment';
import { normalizeRecipientPolicy, type RecipientPolicy } from '../types';

export interface ResolvedRecipient {
  policy: RecipientPolicy;
  email?: string;
  resourceReference?: string;
  display?: string;
  resolved: boolean;
  reason?: string;
}

export interface RecipientResolutionInput {
  patientId: string;
  triggerResourceType?: string;
  triggerResourceId?: string;
}

type RecipientResolver = (
  medplum: MedplumClient,
  input: RecipientResolutionInput,
) => Promise<ResolvedRecipient | ResolvedRecipient[]>;

function extractPatientEmail(patient: Patient): string | undefined {
  const emailTelecom = patient.telecom?.find(
    (t) => t.system === 'email' && t.value,
  );
  return emailTelecom?.value;
}

function getPatientDisplay(patient: Patient): string | undefined {
  const n = patient.name?.[0];
  if (!n) return undefined;
  return `${n.given?.join(' ') ?? ''} ${n.family ?? ''}`.trim() || undefined;
}

function humanNameDisplay(
  name: { given?: string[]; family?: string; text?: string } | undefined,
): string | undefined {
  if (!name) return undefined;
  if (name.text?.trim()) return name.text.trim();
  return `${name.given?.join(' ') ?? ''} ${name.family ?? ''}`.trim() || undefined;
}

async function resolveClientPrimary(
  medplum: MedplumClient,
  input: RecipientResolutionInput,
): Promise<ResolvedRecipient> {
  try {
    const patient = await medplum.readResource('Patient', input.patientId);
    return {
      policy: 'client-primary',
      email: extractPatientEmail(patient),
      resourceReference: `Patient/${input.patientId}`,
      display: getPatientDisplay(patient) ?? `Patient/${input.patientId}`,
      resolved: true,
    };
  } catch {
    return {
      policy: 'client-primary',
      resolved: false,
      reason: `Failed to load Patient/${input.patientId}`,
    };
  }
}

async function resolveAppointmentParticipants(
  medplum: MedplumClient,
  input: RecipientResolutionInput,
): Promise<ResolvedRecipient[]> {
  if (input.triggerResourceType !== 'Appointment' || !input.triggerResourceId) {
    return [
      {
        policy: 'appointment-participants',
        resolved: false,
        reason: 'Appointment participants are only resolvable from an Appointment-triggered rule',
      },
    ];
  }

  try {
    const appointment = (await medplum.readResource(
      'Appointment',
      input.triggerResourceId,
    )) as Appointment;

    const actors = [
      ...getPatientParticipants(appointment),
      ...getRelatedPersonParticipants(appointment),
    ];

    const seenRefs = new Set<string>();
    const recipients: ResolvedRecipient[] = [];

    for (const participant of actors) {
      const ref = participant.actor?.reference;
      if (!ref || seenRefs.has(ref)) continue;
      if (!ref.startsWith('Patient/') && !ref.startsWith('RelatedPerson/')) continue;
      seenRefs.add(ref);

      const [resourceType, id] = ref.split('/');
      if (!resourceType || !id) continue;

      try {
        const resource = await medplum.readResource(resourceType as 'Patient', id);
        const email = extractEmailFromTelecom(
          (resource as { telecom?: { system?: string; value?: string }[] }).telecom,
        );
        const display =
          participant.actor?.display
          ?? humanNameDisplay(
            (resource as { name?: Array<{ given?: string[]; family?: string; text?: string }> }).name?.[0],
          )
          ?? ref;
        recipients.push({
          policy: 'appointment-participants',
          email,
          resourceReference: ref,
          display,
          resolved: true,
        });
      } catch {
        recipients.push({
          policy: 'appointment-participants',
          resourceReference: ref,
          display: participant.actor?.display ?? ref,
          resolved: false,
          reason: `Failed to load ${ref}`,
        });
      }
    }

    if (recipients.length === 0) {
      return [
        {
          policy: 'appointment-participants',
          resolved: false,
          reason: 'Appointment has no Patient or RelatedPerson participants',
        },
      ];
    }

    return recipients;
  } catch {
    return [
      {
        policy: 'appointment-participants',
        resolved: false,
        reason: `Failed to resolve participants for Appointment/${input.triggerResourceId}`,
      },
    ];
  }
}

async function resolveGuardianOrResponsibleParty(
  medplum: MedplumClient,
  input: RecipientResolutionInput,
): Promise<ResolvedRecipient> {
  try {
    const relatedPersons = await medplum.searchResources('RelatedPerson', {
      patient: `Patient/${input.patientId}`,
      _count: '10',
    });
    const guardian = relatedPersons.find((rp: RelatedPerson) =>
      rp.relationship?.some((rel) =>
        rel.coding?.some(
          (c) => c.code === 'GUARD' || c.code === 'guardian' || c.code === 'parent',
        ),
      ),
    ) ?? relatedPersons[0];

    if (!guardian) {
      return {
        policy: 'guardian-or-responsible-party',
        resolved: false,
        reason: 'No guardian or responsible party found for patient',
      };
    }

    const email = guardian.telecom?.find((t) => t.system === 'email')?.value;
    const display = guardian.name?.[0]
      ? `${guardian.name[0].given?.join(' ') ?? ''} ${guardian.name[0].family ?? ''}`.trim()
      : undefined;

    return {
      policy: 'guardian-or-responsible-party',
      email,
      resourceReference: `RelatedPerson/${guardian.id}`,
      display: display || email || `RelatedPerson/${guardian.id}`,
      resolved: true,
    };
  } catch {
    return {
      policy: 'guardian-or-responsible-party',
      resolved: false,
      reason: 'Failed to search for related persons',
    };
  }
}

async function resolveBillingContact(
  _medplum: MedplumClient,
  _input: RecipientResolutionInput,
): Promise<ResolvedRecipient> {
  // TODO: Implement when billing contact data model is available.
  // Billing contacts may be stored as Account.guarantor, Coverage.subscriber,
  // or a RelatedPerson with a billing relationship code.
  return {
    policy: 'billing-contact',
    resolved: false,
    reason: 'Billing contact resolution not yet implemented',
  };
}

async function resolveContactFormSubmitter(
  _medplum: MedplumClient,
  _input: RecipientResolutionInput,
): Promise<ResolvedRecipient> {
  // TODO: Implement when contact form submission model is available.
  // Contact form submitter email may come from the trigger QuestionnaireResponse
  // or a custom extension on the Patient/intake resource.
  return {
    policy: 'contact-form-submitter',
    resolved: false,
    reason: 'Contact form submitter resolution not yet implemented',
  };
}

function extractEmailFromTelecom(
  telecom: { system?: string; value?: string }[] | undefined,
): string | undefined {
  return telecom?.find((t) => t.system === 'email' && t.value)?.value;
}

async function resolveEmailFromReference(
  medplum: MedplumClient,
  ref: string,
): Promise<string | undefined> {
  const [resourceType, id] = ref.split('/');
  if (!resourceType || !id) return undefined;

  try {
    if (resourceType === 'Practitioner') {
      const pract = await medplum.readResource('Practitioner', id) as Practitioner;
      return extractEmailFromTelecom(pract.telecom);
    }
    if (resourceType === 'PractitionerRole') {
      const role = await medplum.readResource('PractitionerRole', id) as PractitionerRole;
      const roleEmail = extractEmailFromTelecom(role.telecom);
      if (roleEmail) return roleEmail;
      if (role.practitioner?.reference) {
        return resolveEmailFromReference(medplum, role.practitioner.reference);
      }
      return undefined;
    }
    if (resourceType === 'RelatedPerson' || resourceType === 'Patient') {
      const resource = await medplum.readResource(resourceType, id);
      return extractEmailFromTelecom((resource as { telecom?: { system?: string; value?: string }[] }).telecom);
    }
  } catch {
    return undefined;
  }
  return undefined;
}

async function resolveCareTeamPrimaryContact(
  medplum: MedplumClient,
  input: RecipientResolutionInput,
): Promise<ResolvedRecipient> {
  try {
    const careTeams = await medplum.searchResources('CareTeam', {
      patient: `Patient/${input.patientId}`,
      status: 'active',
      _count: '1',
    });
    if (careTeams.length === 0) {
      return {
        policy: 'care-team-primary-contact',
        resolved: false,
        reason: 'No active CareTeam found for patient',
      };
    }

    const primaryParticipant = careTeams[0].participant?.find(
      (p) => p.role?.some((r) => r.coding?.some((c) => c.code === 'PPRF' || c.code === 'primary')), // PPRF: the primary performer role CareTeams are written with
    ) ?? careTeams[0].participant?.[0];

    if (!primaryParticipant?.member?.reference) {
      return {
        policy: 'care-team-primary-contact',
        resolved: false,
        reason: 'CareTeam has no primary participant',
      };
    }

    const email = await resolveEmailFromReference(medplum, primaryParticipant.member.reference);

    return {
      policy: 'care-team-primary-contact',
      email,
      resourceReference: primaryParticipant.member.reference,
      display: primaryParticipant.member.display ?? primaryParticipant.member.reference,
      resolved: true,
    };
  } catch {
    return {
      policy: 'care-team-primary-contact',
      resolved: false,
      reason: 'Failed to search CareTeams',
    };
  }
}

/**
 * Project-level configuration for admin Practitioner IDs.
 * In a full deployment this would be loaded from a FHIR
 * `Organization` resource or project-level Parameters resource.
 */
const UNIFY_ADMIN_PRACTITIONER_IDS: string[] = [];

export function setUnifyAdminPractitionerIds(ids: string[]): void {
  UNIFY_ADMIN_PRACTITIONER_IDS.length = 0;
  UNIFY_ADMIN_PRACTITIONER_IDS.push(...ids);
}

async function resolveUnifyAdmin(
  medplum: MedplumClient,
  _input: RecipientResolutionInput,
): Promise<ResolvedRecipient> {
  if (UNIFY_ADMIN_PRACTITIONER_IDS.length === 0) {
    return {
      policy: 'unify-admin',
      resolved: false,
      reason: 'No admin Practitioner IDs configured. Configure via setUnifyAdminPractitionerIds().',
    };
  }

  const practId = UNIFY_ADMIN_PRACTITIONER_IDS[0];
  try {
    const pract = await medplum.readResource('Practitioner', practId) as Practitioner;
    const email = extractEmailFromTelecom(pract.telecom);
    const display = pract.name?.[0]
      ? `${pract.name[0].given?.join(' ') ?? ''} ${pract.name[0].family ?? ''}`.trim()
      : undefined;

    return {
      policy: 'unify-admin',
      email,
      resourceReference: `Practitioner/${practId}`,
      display: display || `Practitioner/${practId}`,
      resolved: true,
    };
  } catch {
    return {
      policy: 'unify-admin',
      resolved: false,
      reason: `Failed to load admin Practitioner/${practId}`,
    };
  }
}

async function resolveTaskOwner(
  medplum: MedplumClient,
  input: RecipientResolutionInput,
): Promise<ResolvedRecipient> {
  if (input.triggerResourceType !== 'Task' || !input.triggerResourceId) {
    return {
      policy: 'task-owner',
      resolved: false,
      reason: 'Task owner is only resolvable from a Task-triggered rule',
    };
  }
  try {
    const task = await medplum.readResource('Task', input.triggerResourceId);
    const ownerRef = task.owner?.reference;
    if (!ownerRef?.startsWith('Practitioner/')) {
      return {
        policy: 'task-owner',
        resolved: false,
        reason: 'Task.owner is not a Practitioner',
      };
    }
    const pract = await medplum.readResource('Practitioner', ownerRef.slice('Practitioner/'.length));
    const email = extractEmailFromTelecom(pract.telecom);
    const display = pract.name?.[0]
      ? `${pract.name[0].given?.join(' ') ?? ''} ${pract.name[0].family ?? ''}`.trim()
      : undefined;
    return {
      policy: 'task-owner',
      email,
      resourceReference: ownerRef,
      display: display || task.owner?.display || ownerRef,
      resolved: true,
    };
  } catch {
    return {
      policy: 'task-owner',
      resolved: false,
      reason: `Failed to resolve owner for Task/${input.triggerResourceId}`,
    };
  }
}

async function resolveAssignedProvider(
  medplum: MedplumClient,
  input: RecipientResolutionInput,
): Promise<ResolvedRecipient> {
  if (input.triggerResourceType !== 'Appointment' || !input.triggerResourceId) {
    return {
      policy: 'assigned-provider',
      resolved: false,
      reason: 'Assigned provider is only resolvable from an Appointment-triggered rule',
    };
  }

  try {
    const appointment = await medplum.readResource('Appointment', input.triggerResourceId);
    const participant = getPractitionerParticipant(appointment);
    const practRef = participant?.actor?.reference;
    if (!practRef) {
      return {
        policy: 'assigned-provider',
        resolved: false,
        reason: 'Appointment has no Practitioner participant',
      };
    }

    const practId = practRef.replace('Practitioner/', '');
    const pract = await medplum.readResource('Practitioner', practId);
    const email = extractEmailFromTelecom(pract.telecom);
    const display = pract.name?.[0]
      ? `${pract.name[0].given?.join(' ') ?? ''} ${pract.name[0].family ?? ''}`.trim()
      : undefined;

    return {
      policy: 'assigned-provider',
      email,
      resourceReference: practRef,
      display: display || participant?.actor?.display || practRef,
      resolved: true,
    };
  } catch {
    return {
      policy: 'assigned-provider',
      resolved: false,
      reason: `Failed to resolve assigned provider for Appointment/${input.triggerResourceId}`,
    };
  }
}

const RESOLVERS: Record<string, RecipientResolver> = {
  'client-primary': resolveClientPrimary,
  'client-primary-email': resolveClientPrimary,
  'appointment-participants': resolveAppointmentParticipants,
  'guardian-or-responsible-party': resolveGuardianOrResponsibleParty,
  'billing-contact': resolveBillingContact,
  'contact-form-submitter': resolveContactFormSubmitter,
  'care-team-primary-contact': resolveCareTeamPrimaryContact,
  'unify-admin': resolveUnifyAdmin,
  'assigned-provider': resolveAssignedProvider,
  'task-owner': resolveTaskOwner,
};

/**
 * Resolve all recipient policies to typed recipients.
 * Deduplicates by resource reference (then email) to prevent duplicate notifications.
 */
export async function resolveRecipients(
  medplum: MedplumClient,
  policies: RecipientPolicy[],
  input: RecipientResolutionInput,
): Promise<ResolvedRecipient[]> {
  const nested = await Promise.all(
    policies.map(async (rawPolicy) => {
      const policy = normalizeRecipientPolicy(rawPolicy);
      const resolver = RESOLVERS[policy];
      if (!resolver) {
        return [
          {
            policy,
            resolved: false,
            reason: `Unknown recipient policy: ${policy}`,
          } satisfies ResolvedRecipient,
        ];
      }
      const resolved = await resolver(medplum, input);
      return Array.isArray(resolved) ? resolved : [resolved];
    }),
  );

  const results = nested.flat();
  const seen = new Set<string>();
  return results.filter((r) => {
    const key = r.resourceReference ?? (r.email ? `email:${r.email.trim().toLowerCase()}` : undefined);
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
